import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import type { SeasonPlayerRecord } from '../api/types.js';
import { bootstrapElapsedMs, recordBootstrapMetric } from './bootstrapPerformance.js';
import {
  buildMatchDayGroups,
  buildPlayerNameMap,
  formatPlayerNameWithHandicap,
  selectLatestCompletedMatchDay,
  selectUpcomingMatchDay,
} from './seasonViews.js';
import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { resolveDashboardRecoveryState } from './recoveryGuidance.js';
import {
  useLeagueMembers,
  useLeagueRole,
  useRequiredLeagueId,
  useResolvedActiveSeason,
  useSyncLeagueFromRoute,
} from './hooks.js';
import { formatDate, formatOneDecimal } from '../lib/formatting.js';
import { calculateCourseAndPlayingHandicapForView } from '../lib/viewScoring.js';

function badge(status: string) {
  return <span className={`badge ${status || 'unknown'}`}>{status || 'unknown'}</span>;
}

function resolveCourseHandicapForSchedule(
  handicapIndex: number | undefined,
  course:
    | {
        slope_rating: number;
        course_rating: number;
        par: number;
      }
    | undefined,
): number | null {
  if (typeof handicapIndex !== 'number' || Number.isNaN(handicapIndex) || !course) {
    return null;
  }

  return calculateCourseAndPlayingHandicapForView(handicapIndex, course).courseHandicapValue;
}

export function DashboardPage() {
  const { client, leagues, activeLeagueId, player, canCreateLeague } = useAppState();
  const leagueId = activeLeagueId ?? '';
  const { season } = useResolvedActiveSeason(leagueId);
  const todayIso = new Date().toISOString().slice(0, 10);
  const dashboardReadyKeyRef = useRef<string | null>(null);

  const league = leagues.find((candidate) => candidate.id === activeLeagueId) ?? null;

  const dashboardQuery = useQuery({
    queryKey: ['dashboardSnapshot', leagueId, season?.id ?? null],
    enabled: Boolean(client && leagueId && season),
    staleTime: 30_000,
    queryFn: async () => client!.getDashboardSnapshot(leagueId, season!.id),
  });
  const isDashboardLoading = Boolean(season) && dashboardQuery.isLoading;
  const dashboardError = dashboardQuery.error;
  const role = dashboardQuery.data?.viewerRole ?? null;
  const { role: membershipRole } = useLeagueRole(leagueId);
  const recoveryState = resolveDashboardRecoveryState({
    leagueId: league?.id ?? null,
    hasPlayer: Boolean(player),
    hasLeagueAccess: Boolean(league),
    hasSeason: Boolean(season),
    role: role ?? membershipRole,
  });

  useEffect(() => {
    if (!league || !season || isDashboardLoading || dashboardError) {
      return;
    }

    const readyKey = `${league.id}:${season.id}`;
    if (dashboardReadyKeyRef.current === readyKey) {
      return;
    }

    dashboardReadyKeyRef.current = readyKey;
    recordBootstrapMetric('dashboardReady', bootstrapElapsedMs() ?? 0, {
      leagueId: league.id,
      seasonId: season.id,
      standingsCount: dashboardQuery.data?.standings.length ?? 0,
      matchDayCount: dashboardQuery.data?.matchDays.length ?? 0,
    });
  }, [
    dashboardError,
    dashboardQuery.data?.matchDays.length,
    dashboardQuery.data?.standings.length,
    isDashboardLoading,
    league,
    season,
  ]);

  if (!league) {
    return (
      <>
        <PageIntro
          title="Dashboard"
        />
        <section className="panel">
          <EmptyState
            title={recoveryState?.title || 'No League Access Yet'}
            message={
              recoveryState?.message ||
              'You are signed in, but you are not currently assigned to a league.'
            }
            action={
              <div className="inline-actions">
                <Link className="nav-link" to={recoveryState?.actionPath || '/'}>
                  {recoveryState?.actionLabel || 'Review Account Setup'}
                </Link>
                {canCreateLeague ? <Link className="nav-link" to="/leagues/new">Create League</Link> : null}
              </div>
            }
          />
        </section>
      </>
    );
  }

  if (isDashboardLoading) {
    return <LoadingPanel title="Dashboard" message="Loading league snapshot." />;
  }

  if (dashboardError) {
    return (
      <ErrorPanel
        title="Dashboard"
        message={mapError(dashboardError)}
      />
    );
  }

  if (recoveryState) {
    return (
      <>
        <PageIntro
          title="Dashboard"
          actions={canCreateLeague ? <Link className="button-link" to="/leagues/new">Create League</Link> : null}
        />
        <section className="panel">
          <EmptyState
            title={recoveryState.title}
            message={recoveryState.message}
            action={
              <Link className="nav-link" to={recoveryState.actionPath}>
                {recoveryState.actionLabel}
              </Link>
            }
          />
        </section>
      </>
    );
  }

  const standings = dashboardQuery.data?.standings ?? [];
  const seasonPlayers = dashboardQuery.data?.seasonPlayers ?? [];
  const courseNameById = new Map((dashboardQuery.data?.courses ?? []).map((course) => [course.id, course.name]));
  const playerNameById = new Map(
    seasonPlayers.map((seasonPlayer) => [seasonPlayer.player_id, seasonPlayer.player?.name || seasonPlayer.player_id]),
  );
  const groupedMatchDays = buildMatchDayGroups(
    dashboardQuery.data?.matchDays ?? [],
    dashboardQuery.data?.matches ?? [],
    courseNameById,
    playerNameById,
  );
  const upcomingMatchDay = selectUpcomingMatchDay(groupedMatchDays, todayIso);
  const latestCompletedMatchDay = selectLatestCompletedMatchDay(groupedMatchDays, todayIso);

  return (
    <>
      <PageIntro
        title="Dashboard"
        actions={
          role === 'admin' || canCreateLeague ? (
            <>
              {canCreateLeague ? <Link className="nav-link" to="/leagues/new">Create League</Link> : null}
              {role === 'admin' ? (
                <>
                  <Link className="nav-link" to={`/leagues/${league.id}/admin/match-days`}>Match Day Setup</Link>
                  <Link className="button-link" to={`/leagues/${league.id}/admin/score-entry`}>Enter Scores</Link>
                </>
              ) : null}
            </>
          ) : null
        }
      />
      <section className="grid two dashboard-feature-grid">
        <article className="panel panel-accent stack">
          <PanelHeader
            title="Upcoming Match Day"
            actions={
              upcomingMatchDay ? (
                <Link className="nav-link" to={`/leagues/${league.id}/matches#match-day-${upcomingMatchDay.id}`}>
                  Open Match Day
                </Link>
              ) : null
            }
          />
          {!upcomingMatchDay ? (
            <EmptyState
              title="No upcoming match day"
              message="Nothing is scheduled for the selected season yet."
              action={
                role === 'admin' ? (
                  <Link className="nav-link" to={`/leagues/${league.id}/admin/match-days`}>
                    Schedule Match Day
                  </Link>
                ) : null
              }
            />
          ) : (
            <div className="week-head"><h3>Week {upcomingMatchDay.weekNumber || '?'} <span className="muted">· {formatDate(upcomingMatchDay.date)} · {upcomingMatchDay.courseName}</span></h3></div>
          )}
        </article>
        <article className="panel stack">
          <PanelHeader
            title="Last Week Results"
            actions={
              latestCompletedMatchDay ? (
                <Link className="nav-link" to={`/leagues/${league.id}/matches#match-day-${latestCompletedMatchDay.id}`}>
                  Open Week
                </Link>
              ) : null
            }
          />
          {!latestCompletedMatchDay ? (
            <EmptyState title="No results yet" message="" />
          ) : (
            <div className="stack">
              <div className="week-head"><h3>Week {latestCompletedMatchDay.weekNumber || '?'} <span className="muted">· {formatDate(latestCompletedMatchDay.date)} · {latestCompletedMatchDay.courseName}</span></h3></div>
              <div className="card-list dashboard-result-list">
                {latestCompletedMatchDay.matches.map((match) => (
                  <div className="card dashboard-result-card" key={match.id}>
                    <div className="dashboard-result-row">
                      <strong>
                        {match.playerAName} vs {match.playerBName}
                      </strong>
                      {badge(match.status)}
                    </div>
                    <div className="dashboard-result-row">
                      <span className="dashboard-result-score">
                        {match.playerAPoints} - {match.playerBPoints}
                      </span>
                      <Link to={`/leagues/${league.id}/matches/${match.id}`}>Open match</Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </article>
        <article className="panel">
          <PanelHeader
            title="Standings"
            actions={
              <Link className="nav-link" to={`/leagues/${league.id}/standings`}>
                View All
              </Link>
            }
          />
          {standings.length === 0 ? (
            <EmptyState title="No standings yet" message="" />
          ) : (
            <StandingsTable leagueId={league.id} standings={standings} />
          )}
        </article>
      </section>
    </>
  );
}

export function StandingsPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client } = useAppState();
  const { season } = useResolvedActiveSeason(leagueId);
  const standingsQuery = useQuery({
    queryKey: ['standings', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getStandings(leagueId, season!.id)).standings,
  });

  if (standingsQuery.isLoading) {
    return <LoadingPanel title="Standings" message="Loading standings." />;
  }

  if (standingsQuery.isError) {
    return (
      <ErrorPanel
        title="Standings"
        message={mapError(standingsQuery.error)}
      />
    );
  }

  const standings = standingsQuery.data ?? [];

  return (
    <>
      <PageIntro
        title="Standings"
      />
      <section className="panel">
        {!season ? (
          <EmptyState title="No active season selected" message="Choose an active season from the header to view standings." />
        ) : standings.length === 0 ? (
          <EmptyState title="No standings yet" message="" />
        ) : (
          <StandingsTable leagueId={leagueId} standings={standings} />
        )}
      </section>
    </>
  );
}

export function RosterPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client } = useAppState();
  const { season } = useResolvedActiveSeason(leagueId);
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, season!.id)).seasonPlayers,
  });

  if (seasonPlayersQuery.isLoading) {
    return <LoadingPanel title="Roster" message="Loading roster." />;
  }

  if (seasonPlayersQuery.isError) {
    return (
      <ErrorPanel
        title="Roster"
        message={mapError(seasonPlayersQuery.error)}
      />
    );
  }

  const seasonPlayers = [...(seasonPlayersQuery.data ?? [])].sort((left, right) =>
    (left.player?.name || left.player_id).localeCompare(right.player?.name || right.player_id),
  );

  return (
    <>
      <PageIntro
        title="Roster"
      />
      <section className="panel">
        {!season ? (
          <EmptyState title="No active season selected" message="Choose an active season from the header to view the roster." />
        ) : seasonPlayers.length === 0 ? (
          <EmptyState title="No players assigned" message="Assign league members to the active season to populate this roster." />
        ) : (
          <SeasonRosterList leagueId={leagueId} seasonPlayers={seasonPlayers} />
        )}
      </section>
    </>
  );
}

export function MatchesPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client } = useAppState();
  const { season } = useResolvedActiveSeason(leagueId);
  const membersQuery = useLeagueMembers(leagueId);
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, season!.id)).seasonPlayers,
  });
  const matchDaysQuery = useQuery({
    queryKey: ['matchDays', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getMatchDays(leagueId, season!.id)).matchDays,
  });
  const coursesQuery = useQuery({
    queryKey: ['courses', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getCourses(leagueId)).courses,
  });
  const seasonMatchesQuery = useQuery({
    queryKey: ['seasonMatches', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonMatches(leagueId, season!.id)).matches,
  });

  if (
    membersQuery.isLoading ||
    seasonPlayersQuery.isLoading ||
    matchDaysQuery.isLoading ||
    coursesQuery.isLoading ||
    seasonMatchesQuery.isLoading
  ) {
    return <LoadingPanel title="Season Schedule" message="Loading schedule." />;
  }

  if (
    membersQuery.isError ||
    seasonPlayersQuery.isError ||
    matchDaysQuery.isError ||
    coursesQuery.isError ||
    seasonMatchesQuery.isError
  ) {
    return (
      <ErrorPanel
        title="Season Schedule"
        message={mapError(
          membersQuery.error ??
            seasonPlayersQuery.error ??
            matchDaysQuery.error ??
            coursesQuery.error ??
            seasonMatchesQuery.error,
        )}
      />
    );
  }

  const seasonPlayers = seasonPlayersQuery.data ?? [];
  const handicapIndexByPlayerId = new Map(
    seasonPlayers.map((seasonPlayer) => [seasonPlayer.player_id, Number(seasonPlayer.current_handicap_index)]),
  );
  const seasonPlayerNameById = new Map(
    seasonPlayers.map((seasonPlayer) => [
      seasonPlayer.player_id,
      seasonPlayer.player?.name || seasonPlayer.player_id,
    ]),
  );
  const memberNameById = seasonPlayerNameById.size > 0 ? seasonPlayerNameById : buildPlayerNameMap(membersQuery.data ?? []);
  const courseById = new Map((coursesQuery.data ?? []).map((course) => [course.id, course]));
  const courseNameById = new Map((coursesQuery.data ?? []).map((course) => [course.id, course.name]));
  const groups = buildMatchDayGroups(
    matchDaysQuery.data ?? [],
    seasonMatchesQuery.data ?? [],
    courseNameById,
    memberNameById,
  );
  const orderedGroups = [...groups].sort((left, right) => {
    const dateDiff = left.date.localeCompare(right.date);
    return dateDiff !== 0 ? dateDiff : left.id.localeCompare(right.id);
  });
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <>
      <PageIntro
        title="Season Schedule"
      />
      {orderedGroups.length === 0 ? (
        <section className="panel">
          <EmptyState title={season ? 'No match days' : 'No active season selected'} message="" />
        </section>
      ) : (
        orderedGroups.map((group) => (
          <section key={group.id} id={`match-day-${group.id}`} className="panel stack match-day-panel">
            <div className="week-head"><h3>Week {group.weekNumber || '?'} <span className="muted">· {formatDate(group.date)} · {group.courseName}</span></h3></div>
            {group.matches.length === 0 ? (
              <EmptyState title="No matches assigned" message="" />
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Player A</th>
                      <th>Player B</th>
                      <th>Status</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.matches.map((match) => (
                      <tr key={match.id}>
                        <td>
                          {group.date >= todayIso && match.status === 'scheduled'
                            ? formatPlayerNameWithHandicap(
                                match.playerAName,
                                handicapIndexByPlayerId.get(match.playerAId),
                                resolveCourseHandicapForSchedule(
                                  handicapIndexByPlayerId.get(match.playerAId),
                                  group.courseId ? courseById.get(group.courseId) : undefined,
                                ),
                              )
                            : match.playerAName}
                        </td>
                        <td>
                          {group.date >= todayIso && match.status === 'scheduled'
                            ? formatPlayerNameWithHandicap(
                                match.playerBName,
                                handicapIndexByPlayerId.get(match.playerBId),
                                resolveCourseHandicapForSchedule(
                                  handicapIndexByPlayerId.get(match.playerBId),
                                  group.courseId ? courseById.get(group.courseId) : undefined,
                                ),
                              )
                            : match.playerBName}
                        </td>
                        <td>{match.status === 'completed' ? null : badge(match.status)}</td>
                        <td>
                          <Link to={`/leagues/${leagueId}/matches/${match.id}`}>
                            {match.playerAPoints} - {match.playerBPoints}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))
      )}
    </>
  );
}

function StandingsTable({
  leagueId,
  standings,
}: {
  leagueId: string;
  standings: Array<{
    playerId: string;
    playerName: string;
    totalPoints: number;
    matchesPlayed: number;
  }>;
}) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Player</th>
            <th>Total Points</th>
            <th>Matches</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((row, index) => (
            <tr key={row.playerId}>
              <td>{index + 1}</td>
              <td>
                <Link to={`/leagues/${leagueId}/players/${row.playerId}`}>{row.playerName}</Link>
              </td>
              <td>{row.totalPoints}</td>
              <td>{row.matchesPlayed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeasonRosterList({
  leagueId,
  seasonPlayers,
}: {
  leagueId: string;
  seasonPlayers: SeasonPlayerRecord[];
}) {
  return (
    <div className="card-list roster-card-list">
      {seasonPlayers.map((seasonPlayer) => (
        <div className="card roster-card" key={seasonPlayer.player_id}>
          <div className="roster-card-main">
            <strong>
              <Link to={`/leagues/${leagueId}/players/${seasonPlayer.player_id}`}>
                {seasonPlayer.player?.name || seasonPlayer.player_id}
              </Link>
            </strong>
            <span className="status-pill">HI {formatOneDecimal(Number(seasonPlayer.current_handicap_index))}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
